import React from 'react';
import { Link } from 'react-router-dom';
import type { GamificationEventListItem } from '@troia-v3/types';

import { Avatar, AvatarFallback, AvatarImage, DataTable, DataTableBody, DataTableCell, DataTableHeadCell, DataTableHeader, DataTableRow, StatusPill, initials, type StatusPillVariant } from '@/shared/components/ui';
import { Icon } from '@/shared/icons';

import { RULE_LABEL, STATUS_LABEL } from './AuditFilters';

/** Chip de status = `StatusPill` do DS (âmbar = pendente, verde = confirmado, neutro + riscado = estornado). */
const STATUS_VARIANT: Record<GamificationEventListItem['status'], StatusPillVariant> = { pending: 'warning', confirmed: 'success', reversed: 'neutral' };

const when = (iso: string): string => new Date(iso).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }).replace(',', '');

const eventLabel = (e: GamificationEventListItem): string => {
  if (e.ruleKey === 'lead_captured') return e.status === 'pending' ? 'Lead captado' : 'Lead captado e confirmado';
  return RULE_LABEL[e.ruleKey];
};

const pointsText = (n: number): string => (n < 0 ? `−${Math.abs(n)}` : `+${n}`);

export const AuditTable: React.FC<{ items: GamificationEventListItem[] }> = ({ items }) => (
  <DataTable data-testid="gamification-audit-table">
    <DataTableHeader>
      <DataTableHeadCell>Quando</DataTableHeadCell>
      <DataTableHeadCell>Vendedor</DataTableHeadCell>
      <DataTableHeadCell>Evento</DataTableHeadCell>
      <DataTableHeadCell>Origem</DataTableHeadCell>
      <DataTableHeadCell className="text-right">Pontos</DataTableHeadCell>
      <DataTableHeadCell>Status</DataTableHeadCell>
      <DataTableHeadCell><span className="sr-only">Abrir</span></DataTableHeadCell>
    </DataTableHeader>
    <DataTableBody>
      {items.map((e) => (
        <DataTableRow key={e.id} data-testid={`gamification-audit-row-${e.id}`}>
          <DataTableCell className="text-xs tabular-nums" style={{ color: 'var(--theme-muted-text)' }}>{when(e.occurredAt)}</DataTableCell>
          <DataTableCell>
            <span className="inline-flex items-center gap-2">
              <Avatar className="h-6 w-6 text-[10px]">{e.userAvatar && <AvatarImage src={e.userAvatar} alt={e.userName} />}<AvatarFallback className="text-[10px]">{initials(e.userName)}</AvatarFallback></Avatar>
              <span className="text-[13px]">{e.userName}</span>
            </span>
          </DataTableCell>
          <DataTableCell className="text-[13px] font-semibold">{eventLabel(e)}</DataTableCell>
          <DataTableCell className="text-xs" style={{ color: 'var(--theme-muted-text)' }}>
            {e.sourceName ?? '—'}{e.sourceDetail ? ` · ${e.sourceDetail}` : ''}
          </DataTableCell>
          <DataTableCell className="text-right font-semibold tabular-nums" style={{ color: e.points < 0 ? 'var(--theme-error-fg)' : 'var(--theme-primary)' }}>{pointsText(e.points)}</DataTableCell>
          <DataTableCell><StatusPill variant={STATUS_VARIANT[e.status]} label={STATUS_LABEL[e.status]} dot={false} className={e.status === 'reversed' ? 'line-through' : ''} data-testid={`gamification-audit-status-${e.id}`} /></DataTableCell>
          <DataTableCell className="text-right">
            {e.link && <Link to={e.link} className="inline-flex items-center" style={{ color: 'var(--theme-primary)' }} aria-label="Abrir origem" data-testid={`gamification-audit-link-${e.id}`}><Icon name="external-link" size={14} /></Link>}
          </DataTableCell>
        </DataTableRow>
      ))}
    </DataTableBody>
  </DataTable>
);
AuditTable.displayName = 'AuditTable';
